import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

/**
 * Tooltip — hover/focus hint on Radix Tooltip. Use `label` for the common
 * case (wraps the trigger in asChild + renders content):
 *
 *   <Tooltip label="Rename"><IconButton …/></Tooltip>
 *
 * Or compose manually with Tooltip/TooltipTrigger/TooltipContent. App-wide
 * timing comes from the single <TooltipProvider> mounted in main.tsx
 * (300ms open delay). Tooltips are supplementary: never the only place
 * information lives.
 */

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, children, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-tooltip max-w-64 rounded-md bg-bg-200 px-2.5 py-1.5 text-xs font-medium text-ink-100 shadow-popover",
        "origin-[--radix-tooltip-content-transform-origin]",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 duration-color ease-standard",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    >
      {children}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export interface TooltipProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
> {
  /** Tooltip text; pass ReactNode only via the compound API. */
  label: React.ReactNode;
  children: React.ReactNode;
}

/** Shorthand: labelled trigger, default side (top). */
function TooltipWithLabel({ label, children, ...props }: TooltipProps) {
  return (
    <Tooltip {...props}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipWithLabel,
};
